export type ExternalLinkTextSegment =
  | {
      type: "text";
      text: string;
    }
  | {
      type: "url";
      text: string;
      url: string;
    };

export type ExternalLinkPreview = {
  url: string;
  domain: string;
  title?: string | null;
  description?: string | null;
  imageUrl?: string | null;
};

export const normalizeExternalLinkPreview = (
  value: unknown,
  fallbackUrl?: string | null,
): ExternalLinkPreview | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const preview = value as Partial<ExternalLinkPreview>;
  const url = normalizeExternalUrl(preview.url ?? fallbackUrl);

  if (!url) {
    return null;
  }

  return {
    url,
    domain:
      typeof preview.domain === "string" && preview.domain.trim()
        ? preview.domain.trim()
        : getExternalUrlDomain(url),
    title: typeof preview.title === "string" && preview.title.trim() ? preview.title.trim() : null,
    description:
      typeof preview.description === "string" && preview.description.trim()
        ? preview.description.trim()
        : null,
    imageUrl: normalizeExternalUrl(preview.imageUrl) ?? null,
  };
};

const EXTERNAL_URL_PATTERN = /\bhttps?:\/\/[^\s<>"'`]+/gi;
const TRAILING_PUNCTUATION_PATTERN = /[.,;:!?]+$/;
const KINDRED_WEB_HOSTS = new Set(["kindredcommunity.app", "www.kindredcommunity.app"]);
const WHATSAPP_CHAT_HOSTS = new Set(["chat.whatsapp.com", "wa.me", "www.wa.me"]);

const trimExternalUrlToken = (token: string) => {
  let next = token.trim().replace(TRAILING_PUNCTUATION_PATTERN, "");

  while (
    (next.endsWith(")") && !next.includes("(")) ||
    (next.endsWith("]") && !next.includes("[")) ||
    (next.endsWith("}") && !next.includes("{"))
  ) {
    next = next.slice(0, -1);
  }

  return next;
};

export const normalizeExternalUrl = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  const trimmed = trimExternalUrlToken(value);

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
};

export const normalizeWhatsAppChatUrl = (value: string | null | undefined) => {
  const normalizedUrl = normalizeExternalUrl(value);
  if (!normalizedUrl) {
    return null;
  }

  const parsed = new URL(normalizedUrl);
  return WHATSAPP_CHAT_HOSTS.has(parsed.hostname.toLowerCase()) ? normalizedUrl : null;
};

export const getExternalUrlDomain = (value: string) => {
  try {
    return new URL(value).hostname.replace(/^www\./i, "");
  } catch {
    return value;
  }
};

export const getKindredAppRoute = (value: string | null | undefined) => {
  const normalizedUrl = normalizeExternalUrl(value);
  if (!normalizedUrl) {
    return null;
  }

  const parsed = new URL(normalizedUrl);
  if (!KINDRED_WEB_HOSTS.has(parsed.hostname)) {
    return null;
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

export const splitTextByExternalUrls = (text: string | null | undefined): ExternalLinkTextSegment[] => {
  if (!text) {
    return [];
  }

  const segments: ExternalLinkTextSegment[] = [];
  let cursor = 0;

  for (const match of text.matchAll(EXTERNAL_URL_PATTERN)) {
    const rawToken = match[0];
    const start = match.index ?? 0;
    const cleanToken = trimExternalUrlToken(rawToken);
    const url = normalizeExternalUrl(cleanToken);

    if (!url) {
      continue;
    }

    if (start > cursor) {
      segments.push({ type: "text", text: text.slice(cursor, start) });
    }

    segments.push({ type: "url", text: cleanToken, url });
    cursor = start + cleanToken.length;
  }

  if (cursor < text.length) {
    segments.push({ type: "text", text: text.slice(cursor) });
  }

  return segments.length ? segments : [{ type: "text", text }];
};

export const extractExternalUrls = (text: string | null | undefined) => {
  const urls: string[] = [];
  const seen = new Set<string>();

  for (const segment of splitTextByExternalUrls(text)) {
    if (segment.type === "url" && !seen.has(segment.url)) {
      seen.add(segment.url);
      urls.push(segment.url);
    }
  }

  return urls;
};

export const getPrimaryExternalUrl = (text: string | null | undefined) =>
  extractExternalUrls(text)[0] ?? null;
